
<template>
  <div class="main-content">
    <Container
      :title="t('route.labelFrameCategory')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          ref="searchFormRef"
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-button
          v-if="this.$hasPermission(['ACTION_vcLabelFrameCategoryEdit'])"
          @click="handleCreate()"
          type="success"
        >
          {{ t('table.add') }}
        </el-button>
        <el-button
          v-if="this.$hasPermission(['ACTION_vcLabelFrameCategoryEdit'])"
          @click="handleEditOrder()"
          type="primary"
        >
          {{ t('labelFrameCategory.updateOrder') }}
        </el-button>
        <el-button
          v-if="selection.length > 0 && this.$hasPermission(['ACTION_vcLabelFrameCategoryEdit'])"
          @click="handleBathDelete()"
          type="danger"
        >
          {{ t('table.bathDelete') }}
        </el-button>
        <SimpleTable
          :isLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :sort="sort"
          :order="order"
          @handleOrder="handleOrder"
          @handleSelection="handleSelection"
        >
          <template
            v-if="this.$hasPermission(['ACTION_vcLabelFrameCategoryEdit'])"
            #default="slotProps"
          >
            <el-button
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
            <el-button
              @click="handleDelete(slotProps.row.id)"
              type="danger"
              size="mini"
            >
              {{ t('common.delete') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[5, 10, 20]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <el-dialog
      v-if="this.$hasPermission(['ACTION_vcLabelFrameCategoryEdit'])"
      :title="t(textMap[dialogStatus])"
      center
      v-model="dialogFormVisible"
      width="45%"
    >
      <NomalContainer>
        <CenterForm
          v-if="dialogStatus === 'create'"
          :formModel="createModel"
          :formStruct="createStruct"
          :formRules="labelFrameCategoryRole"
          @submit="createSubmit"
          @reset="createReset"
        />
        <CenterForm
          v-if="dialogStatus === 'update'"
          :formModel="updateModel"
          :formStruct="updateStruct"
          :formRules="labelFrameCategoryRole"
          @submit="updateNameSubmit"
          @reset="updateNameReset"
        />
        <DraggableOrder
          v-if="dialogStatus === 'updateOrder'"
          :list="updateOrderList"
          :columnStruct="orderStruct"
          @submit="updateOrder"
          @reset="updateOrderReset"
        />
      </NomalContainer>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import DraggableOrder from '@/components/draggable-order/Index.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  LabelFrameCategoryModel,
  LabelFrameCategoryPageSearchModel,
  LabelFrameCategorySearchModel,
  LabelFrameCategoryAddModel,
  LabelFrameCategoryUpdateModel,
  LabelFrameCategoryUpdateOrderListModel,
  LabelFrameCategoryUpdateOrderModel,
  IdsModel
} from '@/model/product/LabelFrameCategoryModel'
import {
  pageSearchLabelFrameCategory,
  addLabelFrameCategory,
  updateLabelFrameCategory,
  removeLabelFrameCategory,
  batchRemoveLabelFrameCategory,
  searchLabelFrameCategory,
  updateLabelFrameCategoryOrder
} from '@/apis/product/labelFrameCategory'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    DraggableOrder,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const searchStruct = [
      { prop: 'categoryName', label: t('labelFrameCategory.categoryName'), placeholder: t('labelFrameCategory.categoryNameP'), component: 'el-input', type: 'text', model: null }
      // { prop: 'categoryOrder', label: t('labelFrameCategory.categoryOrder'), placeholder: t('labelFrameCategory.categoryOrderP'), component: 'el-input', type: 'number', model: null }
    ]

    const createStruct = [
      { prop: 'categoryName', label: t('labelFrameCategory.categoryName'), placeholder: t('labelFrameCategory.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'categoryOrder', label: t('labelFrameCategory.categoryOrder'), placeholder: t('labelFrameCategory.categoryOrderP'), component: 'el-input', type: 'number', model: null }
    ]

    const updateStruct = [
      { prop: 'categoryName', label: t('labelFrameCategory.categoryName'), placeholder: t('labelFrameCategory.categoryNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'categoryOrder', label: t('labelFrameCategory.categoryOrder'), placeholder: t('labelFrameCategory.categoryOrderP'), component: 'el-input', type: 'number', model: null }
    ]

    const labelFrameCategoryRole = {
      categoryName: [{ required: true, message: t('labelFrameCategory.categoryName').concat(t('common.isRequired')), trigger: 'blur' }],
      categoryOrder: [{ required: true, message: t('labelFrameCategory.categoryOrder').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [

      { prop: 'categoryName', label: t('labelFrameCategory.categoryName'), sortable: 'custom' },
      { prop: 'categoryOrder', label: t('labelFrameCategory.categoryOrder'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: LabelFrameCategoryModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: LabelFrameCategoryModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const orderStruct = { id: 'id', name: 'categoryName', order: 'categoryOrder' }
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as LabelFrameCategorySearchModel,
      tableData: Array<LabelFrameCategoryModel>(),
      updateOrderList: Array<LabelFrameCategoryModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'labelFrameCategory.edit',
        create: 'labelFrameCategory.create',
        updateOrder: 'labelFrameCategory.updateOrder'
      },
      dialogFormVisible: false,
      labelFrameCategoryRole: labelFrameCategoryRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as LabelFrameCategoryAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as LabelFrameCategoryUpdateModel,
      editRow: {} as LabelFrameCategoryModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: LabelFrameCategoryPageSearchModel = {
          categoryName: dataMap.searchFormModel.categoryName,
          categoryOrder: dataMap.searchFormModel.categoryOrder,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchLabelFrameCategory(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async createSubmit() {
        const createData: LabelFrameCategoryAddModel = {
          categoryName: dataMap.createModel.categoryName,
          categoryOrder: dataMap.createModel.categoryOrder
        }
        const result = await addLabelFrameCategory(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: LabelFrameCategoryUpdateModel = {
          id: dataMap.updateModel.id,
          categoryName: dataMap.updateModel.categoryName,
          categoryOrder: dataMap.updateModel.categoryOrder
        }
        const result = await updateLabelFrameCategory(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeLabelFrameCategory(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveLabelFrameCategory(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.categoryName = dataMap.editRow.categoryName
        dataMap.updateModel.categoryOrder = dataMap.editRow.categoryOrder
      },
      createReset() {
        console.log('create reset')
      },
      async updateOrder() {
        if (dataMap.updateOrderList.length < 1) {
          ElMessage({
            message: t('common.noData'),
            type: 'error',
            duration: 2000
          })
          return
        }
        const param: LabelFrameCategoryUpdateOrderListModel = {
          list: Array<LabelFrameCategoryUpdateOrderModel>()
        }
        dataMap.updateOrderList.forEach(item => {
          param.list.push({ id: item.id, categoryOrder: item.categoryOrder })
        })
        const result = await updateLabelFrameCategoryOrder(param)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateOrderReset() {
        console.log('update order reset')
      }
    })
    const handleEditOrder = async() => {
      dataMap.dialogStatus = 'updateOrder'
      dataMap.updateOrderList = []
      const params: LabelFrameCategorySearchModel = {
        categoryName: null,
        categoryOrder: null
      }
      const results = await searchLabelFrameCategory(params)
      if (results?.data) {
        dataMap.updateOrderList = results.data
        dataMap.dialogFormVisible = true
      } else {
        console.log(results?.message)
        ElMessage({
          message: t('common.noData'),
          type: 'error',
          duration: 2000
        })
      }
    }
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: LabelFrameCategoryModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.categoryName = row.categoryName
        dataMap.updateModel.categoryOrder = row.categoryOrder
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      orderStruct,
      handleEditOrder,
      ...toRefs(dataMap)
    }
  }
})
</script>
